// Logic hàng chờ Booking, chép từ script board Mint-Booking (phạm vi · tab · tìm · lọc hãng).
import { fold } from '@hana/data';
import type { BookingRequest, BookingTab } from './types';

export const BOOKING_TABS: ReadonlyArray<{ key: BookingTab; label: string }> = [
  { key: 'queue', label: 'Hàng chờ RQ' },
  { key: 'doing', label: 'Đang làm' },
  { key: 'standby', label: 'Standby' },
  { key: 'stock', label: 'Stock' },
  { key: 'amend', label: 'Amend' },
  { key: 'notify', label: 'Cần báo khách' },
  { key: 'done', label: 'Đã xong' },
];

/** Người nghỉ hôm nay → người thay (board: NHITHT nghỉ, PHUONGHT thay). */
export const BOOKING_COVER: Readonly<Record<string, string>> = { NHITHT: 'PHUONGHT' };

export type BookingScope = 'mine' | 'team' | 'un' | 'cover';

export interface BookingQuery {
  /** người đang xem */
  me: string;
  scope?: BookingScope;
  tab?: BookingTab;
  q?: string;
  carriers?: string[];
}

function inScope(r: BookingRequest, scope: BookingScope, me: string): boolean {
  switch (scope) {
    case 'team': return true;
    case 'mine': return r.owner === me;
    case 'un': return !r.owner;
    case 'cover': return r.owner !== null && BOOKING_COVER[r.owner] === me;
  }
}

/** Lọc theo phạm vi + tìm + hãng (chưa lọc tab, để đếm chip từng tab). */
export function scopeBookings(rows: readonly BookingRequest[], query: BookingQuery): BookingRequest[] {
  const n = fold(query.q ?? '').trim();
  return rows.filter(
    (r) =>
      inScope(r, query.scope ?? 'mine', query.me) &&
      (!query.carriers?.length || query.carriers.includes(r.carrier)) &&
      (!n || fold(`${r.id} ${r.jobId} ${r.customer} ${r.bookingNo ?? ''}`).includes(n)),
  );
}

export function queryBookings(rows: readonly BookingRequest[], query: BookingQuery): BookingRequest[] {
  const tab = query.tab ?? 'notify';
  return scopeBookings(rows, query).filter((r) => r.tab === tab);
}

export function bookingTabCounts(rows: readonly BookingRequest[], query: Omit<BookingQuery, 'tab'>): Record<BookingTab, number> {
  const scoped = scopeBookings(rows, query);
  const out = {} as Record<BookingTab, number>;
  for (const t of BOOKING_TABS) out[t.key] = scoped.filter((r) => r.tab === t.key).length;
  return out;
}

export interface BookingGroup {
  /** khoá trong BOOKING_VESSELS */
  vessel: string;
  rows: BookingRequest[];
}

/** Nhóm theo tàu: thứ tự `order` (ETD gần nhất trước), "Chưa chọn chuyến" (`none`) ở cuối. */
export function groupByVessel(rows: readonly BookingRequest[], order: readonly string[]): BookingGroup[] {
  const by = new Map<string, BookingRequest[]>();
  for (const r of rows) by.set(r.vessel, [...(by.get(r.vessel) ?? []), r]);
  const rank = (v: string) => (v === 'none' ? order.length + 1 : order.includes(v) ? order.indexOf(v) : order.length);
  return [...by.entries()].sort((a, b) => rank(a[0]) - rank(b[0])).map(([vessel, list]) => ({ vessel, rows: list }));
}

/** Tab của màn Yêu cầu (RLT trong board): 6 tab đầu nằm trên thanh, còn lại trong menu "All ▾". */
export type RlTab = 'rqall' | 'rqnew' | 'rqproc' | 'standby' | 'notify' | 'prio' | 'hen' | 'rodp' | 'amend' | 'done' | 'stock' | 'all';
export const RL_TABS: ReadonlyArray<{ key: RlTab; label: string }> = [
  { key: 'rqall', label: 'Tất cả RQ' },
  { key: 'rqnew', label: 'RQ New' },
  { key: 'rqproc', label: 'RQ Processing' },
  { key: 'standby', label: 'Standby' },
  { key: 'notify', label: 'Cần báo khách' },
  { key: 'prio', label: 'Ưu tiên' },
  { key: 'hen', label: 'Hẹn book' },
  { key: 'rodp', label: 'ROD chờ' },
  { key: 'amend', label: 'Amend' },
  { key: 'done', label: 'Đã xong' },
  { key: 'stock', label: 'Stock' },
  { key: 'all', label: 'All' },
];
export const RL_MAIN: readonly RlTab[] = ['rqall', 'rqnew', 'rqproc', 'standby', 'notify', 'prio'];
/** Số dòng Stock (trả chỗ) — board cố định 4, chưa có nguồn riêng. */
export const STOCK_COUNT = 4;

const isPrio = (r: BookingRequest) => r.sourceStatus !== null;
const RL_PRED: Record<RlTab, (r: BookingRequest) => boolean> = {
  rqall: (r) => ['queue', 'doing', 'amend'].includes(r.tab) || r.sourceStatus === 'cancel',
  rqnew: (r) => (r.tab === 'queue' || r.tab === 'amend') && r.sourceStatus !== 'cancel',
  rqproc: (r) => r.tab === 'doing' && r.sourceStatus === null,
  standby: (r) => r.tab === 'standby' && r.sourceStatus !== 'cancel',
  notify: (r) => r.tab === 'notify' && r.sourceStatus !== 'cancel',
  prio: isPrio,
  hen: (r) => r.flags.includes('hen'),
  rodp: (r) => r.flags.includes('rodp'),
  amend: (r) => r.tab === 'amend' && r.sourceStatus !== 'cancel',
  done: (r) => r.tab === 'done' && r.sourceStatus !== 'cancel',
  stock: () => false,
  all: () => true,
};

export function rlTabCounts(scoped: readonly BookingRequest[]): Record<RlTab, number> {
  const out = {} as Record<RlTab, number>;
  for (const t of RL_TABS) out[t.key] = t.key === 'stock' ? STOCK_COUNT : scoped.filter(RL_PRED[t.key]).length;
  return out;
}
export const rlFilter = (scoped: readonly BookingRequest[], tab: RlTab): BookingRequest[] => scoped.filter(RL_PRED[tab]);

/** Số trên chip phạm vi "Booking Staff" (không tính dòng đã xong). */
export function rlOwnCounts(rows: readonly BookingRequest[], me: string): Record<BookingScope, number> {
  const open = rows.filter((r) => r.tab !== 'done');
  return { mine: open.filter((r) => r.owner === me).length, cover: open.filter((r) => r.owner !== null && BOOKING_COVER[r.owner] === me).length, un: open.filter((r) => !r.owner).length, team: open.length };
}

/** Dải "Việc hôm nay": mã · nhãn · số · tab mở khi bấm. BKO / STK là số cố định của board (chưa có nguồn). */
export function todayStrip(c: Record<RlTab, number>): Array<{ code: string; k: string; n: number; go: RlTab; tone: 'link' | 'warn' | 'lb' }> {
  return [
    { code: 'BKO', k: 'Mở book hôm nay', n: 1, go: 'rqproc', tone: 'link' },
    { code: 'VCN', k: 'Báo khách đổi lịch', n: c.notify, go: 'notify', tone: 'warn' },
    { code: 'STK', k: 'Trả chỗ ≤ 48 giờ', n: 3, go: 'stock', tone: 'warn' },
    { code: 'RQU', k: 'Amend chờ xử lý', n: c.amend, go: 'amend', tone: 'link' },
    { code: 'SBY', k: 'Rà Standby · thứ Hai 26/Oct', n: c.standby, go: 'standby', tone: 'lb' },
  ];
}

/** "Bây giờ" của dữ liệu mẫu board (22/Oct/2026 10:00 UTC); dữ liệu thật về sau lấy đồng hồ máy chủ (D-06). Đếm ngược theo giờ hãng tàu, không phải giờ làm việc. */
export const BOARD_NOW = Date.UTC(2026, 9, 22, 10, 0) / 60000;
const MON: Record<string, number> = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };

export interface Countdown { k: string; tip: string; mins: number; tone: 'err' | 'warn' | 'lb' }

/** Đếm ngược tới mốc "dd/Mon[/yyyy] hh:mm" (board `cdOf`). `done` = đã xong thì mốc quá hạn chỉ ghi "đã qua". */
export function countdown(s: string | null, done: boolean, now: number = BOARD_NOW): Countdown | null {
  const m = /^(\d{2})\/(\w{3})(?:\/\d{4})? (\d{2}):(\d{2})/.exec(s ?? '');
  if (!m) return null;
  const mins = Date.UTC(2026, MON[m[2]!] ?? 9, +m[1]!, +m[3]!, +m[4]!) / 60000 - now;
  const a = Math.abs(mins);
  const d = Math.floor(a / 1440), h = Math.floor((a % 1440) / 60), mm = Math.round(a % 60);
  const span = d ? `${d} ngày ${h}h` : h ? `${h}h${String(mm).padStart(2, '0')}′` : `${mm}′`;
  if (mins < 0) return { k: done ? 'đã qua' : `−${span}`, tip: done ? 'đã qua' : `quá ${span}`, mins, tone: done ? 'lb' : 'err' };
  return { k: span, tip: `còn ${span}`, mins, tone: mins <= 360 ? 'err' : mins <= 1440 ? 'warn' : 'lb' };
}

/** Chip cut-off của dòng: SI nếu còn hạn, ngược lại CY (board `mkRL`). */
export function rowCutoff(r: BookingRequest): { label: 'SI' | 'CY'; cd: Countdown; tip: string } | null {
  const si = countdown(r.siCutoff, r.tab === 'done' || r.tab === 'notify');
  const cy = countdown(r.cyCutoff, r.tab === 'done');
  if (si && si.mins > 0) return { label: 'SI', cd: si, tip: `Cut-off SI ${r.siCutoff} · ${si.tip} · giờ hãng tàu` };
  if (cy) return { label: 'CY', cd: cy, tip: `Closing CY ${r.cyCutoff} · ${cy.tip} · giờ hãng tàu` };
  return null;
}

/** ShipmentStatus + RQStatus của dòng (board `shipOf` / `rqOf`). */
export function shipStatus(r: BookingRequest): { k: string; dot: 'amber' | 'blue' | 'gray' } {
  if (r.sourceStatus === 'cancel') return { k: 'Cancelled', dot: 'gray' };
  if (r.sourceStatus === 'waitrls') return { k: 'BK Wait RLS', dot: 'blue' };
  if (r.tab === 'done' || r.tab === 'notify') return { k: 'BK Confirmed', dot: 'blue' };
  if (r.tab === 'queue') return { k: 'RQ Request', dot: 'amber' };
  return { k: r.bookingNo ? 'BK Standby' : 'RQ Standby', dot: 'amber' };
}
const RQ_LABEL: Record<BookingTab, string> = { queue: 'RQ New', doing: 'RQ Processing', standby: 'BK Standby', amend: 'RQ New Amend', notify: 'BK Confirmed', done: 'BK Confirmed', stock: 'BK Standby' };
export const rqStatus = (r: BookingRequest): string => (r.sourceStatus === 'cancel' ? 'RQ Cancel' : r.sourceStatus === 'waitrls' ? 'BK Wait RLS' : RQ_LABEL[r.tab]);

/** Kênh đặt chỗ theo hãng (board `CH`): mặc định Spot. */
export const carrierChannel = (carrier: string): string => (carrier === 'ONE' || carrier === 'COS' ? 'Official' : 'Spot');
